// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever leaves the device; only the small derived remembrance slice (computeSignals)
// is sent for the ONE AI call (see brain.ts).
//
// Data model (mirrors src-tauri/src/db.rs):
//   pet     { name, kind, photo, birth_on, farewell_on }
//   moment  { happened_on, title, caption, photo, place, is_milestone }

import { invoke } from "./tauri-web";

// ── Pet (the single cherished/remembered subject) ──────────────────────────────
export interface Pet {
  id: string;
  name: string;
  /** "dog" / "cat" / … lowercase free text, or "". */
  kind: string;
  /** Photo reference (file path / asset ref), or "". Never bytes. */
  photo: string;
  /** Birth / gotcha date "YYYY-MM-DD", or "". */
  birth_on: string;
  /** Farewell date "YYYY-MM-DD" (the day they passed), or "" if still here. */
  farewell_on: string;
  created_at: string;
  updated_at: string;
}
export type PetPatch = Partial<{
  name: string;
  kind: string;
  photo: string;
  birth_on: string;
  farewell_on: string;
}>;

// ── Moment (a saved life entry) ─────────────────────────────────────────────────
export interface Moment {
  id: string;
  /** Which pet this belongs to (multi-pet, 1.3.0). */
  pet_id: string;
  /** Calendar date the moment happened (local tz), "YYYY-MM-DD". */
  happened_on: string;
  /** Short title SHE wrote ("First snow day"). "" when none. */
  title: string;
  /** Free-text caption SHE wrote. "" when none. */
  caption: string;
  /** Photo reference (file path / asset ref), or "". Never bytes. */
  photo: string;
  /** Place label SHE wrote, normalized lowercase ("the couch"), or "". */
  place: string;
  /** True for a marked milestone (the big beats of a life). */
  is_milestone: boolean;
  created_at: string;
  updated_at: string;
}
export interface NewMoment {
  happened_on?: string;
  title?: string;
  caption?: string;
  photo?: string;
  place?: string;
  is_milestone?: boolean;
}
export type MomentPatch = Partial<{
  happened_on: string;
  title: string;
  caption: string;
  photo: string;
  place: string;
  is_milestone: boolean;
}>;

// ── Derived remembrance slice (computed on-device in Rust; only aggregates + HER
// own short words are sent to the brain). Mirrors src-tauri/src/signals.rs. ───────
export interface PetFacts {
  name: string;
  kind: string;
  has_birth_date: boolean;
  has_farewell_date: boolean;
  years_lived: number | null;
  is_remembered: boolean;
}
export interface Overall {
  total_moments: number;
  milestone_count: number;
  ordinary_count: number;
  moments_with_photo: number;
  span_years: number;
  span_days: number;
  first_moment_on: string | null;
  last_moment_on: string | null;
  distinct_days: number;
}
export interface MilestoneTitle { on: string; title: string }
export interface Milestones { count: number; titles: MilestoneTitle[] }
export interface MomentLite {
  on: string;
  title: string;
  caption_excerpt: string;
  place: string;
  is_milestone: boolean;
}
export interface PlaceCount { place: string; count: number }
export interface MonthCount { month: string; count: number }
export interface YearCount { year: number; count: number }

/** Gentle tone for subtle UI tinting of a fact — NOT advice. */
export type FactTone = "warm" | "tender" | "neutral";
/** A pre-ranked, ready-to-narrate observational fact (computed in Rust). */
export interface HeadlineFact {
  kind: "span" | "milestone" | "place" | "rhythm" | "ordinary" | "pet" | "focus";
  text: string;
  tone: FactTone;
}
/** What the owner re-wove around (a year / place / milestone). */
export interface Focus {
  kind: "year" | "place" | "milestone";
  value: string;
  matched_moments: number;
}
/** The focus the UI sends INTO computeSignals (no computed count). */
export interface FocusInput {
  kind: "year" | "place" | "milestone";
  value: string;
}
export interface RemembranceSignals {
  schema: string;
  generated_for: string;
  pet: PetFacts;
  overall: Overall;
  milestones: Milestones;
  sampled_moments: MomentLite[];
  recurring_places: PlaceCount[];
  month_spread: MonthCount[];
  year_spread: YearCount[];
  focus: Focus | null;
  headline_facts: HeadlineFact[];
}

// ── Care log (2026-08-11 course correction) ─────────────────────────────────────
// The LIVING half. A memory book is written once; a weight, a vet visit or a course of
// medication is what an owner actually records week to week — and what a vet asks for.
export type CareKind = "weight" | "vet" | "med" | "symptom" | "note";
export interface Care {
  id: string;
  /** Which pet this belongs to (multi-pet, 1.3.0). */
  pet_id: string;
  /** Calendar date it happened (local tz), "YYYY-MM-DD". */
  happened_on: string;
  kind: CareKind;
  title: string;
  detail: string;
  /** Weight in kg. null for every kind except "weight". */
  value_num: number | null;
  /** Next-due date "YYYY-MM-DD", or "" when nothing is scheduled. Drives the local reminder. */
  due_on: string;
  created_at: string;
  updated_at: string;
}
export interface NewCare {
  happened_on?: string;
  kind?: CareKind;
  title?: string;
  detail?: string;
  value_num?: number | null;
  due_on?: string;
}
export type CarePatch = Partial<{
  happened_on: string;
  kind: CareKind;
  title: string;
  detail: string;
  value_num: number | null;
  due_on: string;
}>;

export const CARE_KINDS: { kind: CareKind; label: string; hint: string }[] = [
  { kind: "weight", label: "Weight", hint: "kg" },
  { kind: "vet", label: "Vet visit", hint: "What the vet said" },
  { kind: "med", label: "Medication", hint: "What, and how much" },
  { kind: "symptom", label: "Symptom", hint: "What you noticed, and when it started" },
  { kind: "note", label: "Note", hint: "Anything worth remembering" },
];

export interface WeightPoint { on: string; kg: number }
export interface WeightTrend {
  points: WeightPoint[];
  latest: WeightPoint | null;
  min: number;
  max: number;
  /** kg change from the first to the latest reading. null until there are two. */
  changeKg: number | null;
}

/**
 * Weight over time, oldest-first, from the care log.
 *
 * Honesty guard: `changeKg` stays null with fewer than two readings — one weigh-in is a fact,
 * not a trend, and drawing a direction from it would be inventing information the owner
 * never gave us. Same discipline as Journal's trends.
 */
export function buildWeightTrend(rows: Care[]): WeightTrend {
  const points = rows
    .filter((r) => r.kind === "weight" && typeof r.value_num === "number" && isFinite(r.value_num))
    .map((r) => ({ on: r.happened_on, kg: r.value_num as number }))
    .sort((a, b) => a.on.localeCompare(b.on));
  if (!points.length) return { points, latest: null, min: 0, max: 0, changeKg: null };
  const kgs = points.map((p) => p.kg);
  return {
    points,
    latest: points[points.length - 1],
    min: Math.min(...kgs),
    max: Math.max(...kgs),
    changeKg: points.length >= 2 ? points[points.length - 1].kg - points[0].kg : null,
  };
}

/**
 * The pet currently on screen. Multi-pet (1.3.0) needs a "which one" for every data call, and
 * that is pure UI state — it belongs in localStorage, not in the database. `null` means "the
 * first pet", which is exactly what every single-pet household means and what pre-1.3.0 callers
 * meant, so the whole app keeps working untouched for anyone with one animal.
 */
const ACTIVE_PET_KEY = "oriora.foreverpaw.activePetId";
export function loadActivePetId(): string | null {
  try { return localStorage.getItem(ACTIVE_PET_KEY) || null; } catch { return null; }
}
export function saveActivePetId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_PET_KEY, id);
    else localStorage.removeItem(ACTIVE_PET_KEY);
  } catch { /* private mode — the app still works, it just forgets the choice */ }
}

export const store = {
  // pet
  getPet: (id?: string | null) => invoke<Pet | null>("get_pet", { id: id ?? null }),
  listPets: () => invoke<Pet[]>("list_pets"),
  createPet: (patch: PetPatch) => invoke<Pet>("create_pet", { patch }),
  /** Deletes the pet AND every moment and record belonging to it. Confirm before calling. */
  deletePet: (id: string) => invoke<void>("delete_pet", { id }),
  upsertPet: (patch: PetPatch, id?: string | null) => invoke<Pet>("upsert_pet", { id: id ?? null, patch }),

  // care log
  listCare: (petId?: string | null, kind?: CareKind, limit?: number) =>
    invoke<Care[]>("list_care", { petId: petId ?? null, kind, limit }),
  getCare: (id: string) => invoke<Care | null>("get_care", { id }),
  createCare: (input: NewCare, petId?: string | null) =>
    invoke<Care>("create_care", { petId: petId ?? null, input }),
  updateCare: (id: string, patch: CarePatch) => invoke<void>("update_care", { id, patch }),
  deleteCare: (id: string) => invoke<void>("delete_care", { id }),
  // moments
  listMoments: (opts?: { petId?: string | null; from?: string; to?: string; limit?: number }) =>
    invoke<Moment[]>("list_moments", { petId: opts?.petId ?? null, from: opts?.from, to: opts?.to, limit: opts?.limit ?? 50000 }),
  getMoment: (id: string) => invoke<Moment | null>("get_moment", { id }),
  createMoment: (input: NewMoment, petId?: string | null) =>
    invoke<Moment>("create_moment", { petId: petId ?? null, input }),
  updateMoment: (id: string, patch: MomentPatch) => invoke<void>("update_moment", { id, patch }),
  deleteMoment: (id: string) => invoke<void>("delete_moment", { id }),
  /** Copy a picked photo (DESKTOP file-picker path) into the app's local photos/ dir. */
  savePhotoFromPath: (srcPath: string) => invoke<string>("save_photo_from_path", { srcPath }),
  /** Save picked photo bytes (iOS <input type=file>) into the app's local photos/ dir. */
  savePhotoBytes: (bytes: number[], ext: string) => invoke<string>("save_photo_bytes", { bytes, ext }),
  /** Compute the derived story slice on-device. `today` = client local YYYY-MM-DD.
   *  `focus` (optional) narrows the narration to one year / place / milestone.
   *  `petId` scopes it — unscoped it would weave every pet into one life. */
  computeSignals: (today: string, focus?: FocusInput, petId?: string | null) =>
    invoke<RemembranceSignals>("compute_signals", { today, focus: focus ?? null, petId: petId ?? null }),
};

// ── Pure date helpers ──────────────────────────────────────────────────────────
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Friendly long date for display ("Mar 3, 2018"). */
export function prettyDate(s: string): string {
  if (!s) return "";
  return new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
  });
}

/** Curated place suggestions; the user can also type her own. */
export const DEFAULT_PLACES = [
  "home", "the couch", "the garden", "the park", "the beach",
  "the car", "grandma's", "the vet", "the kitchen", "bed",
];

/** Normalize a place label — lowercase, trimmed, single-spaced. */
export function normalizePlace(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/** "11 years" / "8 months" / "3 weeks" — the span of saved moments, in human words. */
export function spanCopy(o: Overall): string {
  if (o.total_moments === 0) return "No moments saved yet";
  if (o.span_years >= 1) return `${o.span_years} ${o.span_years === 1 ? "year" : "years"} of moments`;
  const months = Math.max(1, Math.round(o.span_days / 30.4));
  if (months >= 1 && o.span_days >= 25) return `${months} ${months === 1 ? "month" : "months"} of moments`;
  const weeks = Math.max(1, Math.round(o.span_days / 7));
  return `${weeks} ${weeks === 1 ? "week" : "weeks"} of moments`;
}

/** Title-case a place for display ("the couch" → "The couch"). */
export function displayPlace(p: string): string {
  if (!p) return "";
  return p.charAt(0).toUpperCase() + p.slice(1);
}
